import { useCallback, useState, useEffect, ChangeEvent } from 'react';

import styled from '@emotion/styled';
import { Group, Loader, Radio, Stack, Text, Tooltip } from '@mantine/core';

import { DIFFERENTIAL_MODES, DIFFERENTIAL_MODES_FILTER_OPTIONS } from 'lib/constants';
import { shortFormatDate } from 'lib/helpers';
import { useDifferentialMode, useDifferentialAnomalyData } from 'lib/hooks';

import DifferentialFilterOption from './DifferentialFilterOption';

const DifferentialFilterSection = () => {
    const { mode, setMode, currentInspectionDate, previousInspectionDate } = useDifferentialMode();
    const { isLoadingDifferentialData, isUsingFallbackMatching } = useDifferentialAnomalyData();

    const currentInspectionDateText = shortFormatDate(currentInspectionDate);
    const previousInspectionDateText = shortFormatDate(previousInspectionDate);

    const [inspectionFilter, setInspectionFilter] = useState(currentInspectionDateText);

    useEffect(() => {
        if ([DIFFERENTIAL_MODES.CHANGED_CURRENT, DIFFERENTIAL_MODES.CHANGED_PREVIOUS].includes(mode)) {
            setInspectionFilter(
                mode === DIFFERENTIAL_MODES.CHANGED_CURRENT ? currentInspectionDateText : previousInspectionDateText,
            );
        }
    }, [mode, currentInspectionDateText, previousInspectionDateText]);

    const handleInspectionDateChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const selectedDate = event.currentTarget.value;

            setInspectionFilter(selectedDate);
            const newMode =
                selectedDate === currentInspectionDateText
                    ? DIFFERENTIAL_MODES.CHANGED_CURRENT
                    : DIFFERENTIAL_MODES.CHANGED_PREVIOUS;

            setMode(newMode);
        },
        [currentInspectionDateText, setMode],
    );

    return (
        <Stack pt="0.5rem" spacing="0">
            {isLoadingDifferentialData && isUsingFallbackMatching && (
                <FallbackIndicator>
                    <Loader size="xs" color="gray.1" />
                    <Tooltip
                        label="Different draft versions detected. Using advanced position-based matching, which may take longer."
                        multiline
                        width={220}
                    >
                        <Text variant="body2" c="white" style={{ cursor: 'help' }}>
                            Using advanced matching...
                        </Text>
                    </Tooltip>
                </FallbackIndicator>
            )}
            <FilterSectionContainer spacing="0.25rem">
                {Object.entries(DIFFERENTIAL_MODES_FILTER_OPTIONS).map(([key, value]) => (
                    <DifferentialFilterOption key={key} optionKey={key} optionValue={value} />
                ))}
            </FilterSectionContainer>
            {(mode === DIFFERENTIAL_MODES.CHANGED_CURRENT || mode === DIFFERENTIAL_MODES.CHANGED_PREVIOUS) && (
                <FilterSectionContainer spacing="0.25rem">
                    <Text variant="body1" c="gray.1">
                        Filter by
                    </Text>
                    {[currentInspectionDateText, previousInspectionDateText].map((option) => (
                        <Group key={option} align="center">
                            <Radio
                                key={option}
                                value={option}
                                checked={inspectionFilter === option}
                                onChange={handleInspectionDateChange}
                                styles={{ radio: { '&:hover': { cursor: 'pointer' } } }}
                            />
                            <Text variant="body1" c="gray.1">
                                {option}
                            </Text>
                        </Group>
                    ))}
                </FilterSectionContainer>
            )}
        </Stack>
    );
};

export default DifferentialFilterSection;

const FilterSectionContainer = styled(Stack)({
    borderBottom: '1px solid var(--color-grey-500)',
    padding: '1rem 0',
});

const FallbackIndicator = styled(Group)({
    backgroundColor: 'var(--color-grey-500)',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    marginBottom: '0.5rem',
    gap: '0.5rem',
});
